import { Form, Popup } from 'devextreme-react';
import {
  ButtonItem,
  ButtonOptions,
  CustomRule,
  GroupItem,
  Label,
  SimpleItem,
} from 'devextreme-react/form';
import { RequiredRule } from 'devextreme-react/validator';
import React, { useEffect, useRef, useState } from 'react';
import '../../assets/bulk-modal.scss';
import { useLoading } from '../../contexts/loadingContext';
import { useTranslation } from '../../contexts/translation';
import { DataGridActionType } from '../../types/dataGridActionTypes';
import { BaseValueDataType } from '../../types/parameter.types';
import {
  dateFieldOptionsWithSerialization,
  dateTimeFieldOptionsWithSerialization,
} from '../../utils/dates.types';
import { validationNullableDateRule } from '../../utils/dateValidation';
import './EditCreateModal.scss';
import './TasksBulkEditModal.scss';

type Props = {
  data: any;
  isVisible: boolean;
  mutate?: () => void;
  onClose: () => void;
  onSubmit: (data: any, actionType: DataGridActionType) => void;
  actionType: DataGridActionType;
  transFormName: string;
  parameters: any;
  parameterChanged: any;
  units: any;
  selectedUnitId?: number;
};

/**
 * Modalová komponent pro vytváření/editaci záznamů typu Asset cycle v datagridu
 */
export default function AssetCycleParameterModal({
  isVisible,
  data,
  actionType,
  onClose,
  onSubmit,
  parameters,
  parameterChanged,
  units,
  selectedUnitId,
  transFormName,
}: Props) {
  const { translate } = useTranslation();
  const formRef = useRef<Form>(null);
  const { loading } = useLoading();
  const [selectedUnit, setSelectedUnit] = useState<string>();
  const [scheduleType, setScheduleType] = useState<string | null>(data ? data.type : null);
  const [formData, setFormData] = useState<any>({
    ...data,
    ...(actionType === DataGridActionType.Insert && { visible: true }),
  });

  useEffect(() => {
    setFormData({
      ...data,
      ...(actionType === DataGridActionType.Insert && { visible: true }),
    });
  }, []);

  useEffect(() => {
    if (!data || !units) return;
    setUnitType(data.unitId);
  }, []);

  useEffect(() => {
    if (!selectedUnitId || data) return;
    setUnitType(selectedUnitId);
    setFormData({ ...formData, unitId: selectedUnitId });
  }, [selectedUnitId]);

  const setUnitType = (selectedUnitId: number) => {
    let tmpUnit = units.find((u: any) => u.id === selectedUnitId);
    if (tmpUnit) setSelectedUnit(tmpUnit.baseValueDataType);
  };

  return (
    <Popup
      visible={isVisible}
      dragEnabled={false}
      closeOnOutsideClick
      onHiding={onClose}
      showTitle
      title={translate!('Asset cycle parameters', transFormName)}
      width={'20%'}
      height={'auto'}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formData, actionType);
        }}
      >
        <Form
          formData={formData}
          showColonAfterLabel={true}
          showValidationSummary={true}
          ref={formRef}
        >
          <GroupItem>
            <GroupItem visible={actionType === DataGridActionType.Insert}>
              <SimpleItem
                dataField="assetParameterId"
                editorType="dxSelectBox"
                editorOptions={{
                  items: parameters,
                  valueExpr: 'id',
                  displayExpr: 'parameterName',
                  onValueChanged: (val) => {
                    const param = parameters.find((x) => x.id === val.value);
                    if (param.unitBaseValueDataType === 'Date') {
                      setScheduleType('Daily');
                      setFormData({ ...formData, assetCycleScheduleType: 'Daily' });
                    }

                    parameterChanged(val);
                  },
                }}
              >
                <Label text={translate!('Parameter', transFormName)} />
                <RequiredRule message={translate!('Parameter is required', transFormName)} />
              </SimpleItem>
            </GroupItem>
            <GroupItem visible={actionType === DataGridActionType.Update}>
              <SimpleItem
                dataField="parameterName"
                editorType="dxTextBox"
                editorOptions={{
                  readOnly: true,
                }}
              >
                <Label text={translate!('Parameter', transFormName)} />
              </SimpleItem>
            </GroupItem>
            <GroupItem visible={actionType === DataGridActionType.Insert}>
              <SimpleItem
                dataField="unitId"
                editorType="dxSelectBox"
                editorOptions={{
                  readOnly: true,
                  items: units,
                  valueExpr: 'id',
                  displayExpr: 'name',
                }}
              >
                <Label text={translate!('Unit', transFormName)} />
              </SimpleItem>
            </GroupItem>
            {scheduleType && (
              <GroupItem>
                <SimpleItem
                  dataField="assetCycleScheduleType"
                  editorType="dxSelectBox"
                  editorOptions={{
                    items: [
                      { id: 'Daily', label: translate!('Daily', transFormName) },
                      { id: 'Weekly', label: translate!('Weekly', transFormName) },
                      { id: 'Monthly', label: translate!('Monthly', transFormName) },
                    ],
                    valueExpr: 'id',
                    displayExpr: 'label',
                    onValueChanged: (e) => {
                      setScheduleType(e.value);
                    },
                  }}
                >
                  <Label text={translate!('Type', transFormName)} />
                </SimpleItem>
              </GroupItem>
            )}

            <GroupItem>
              <SimpleItem dataField="period" editorType="dxNumberBox">
                <Label text={translate!('Period', transFormName)} />
              </SimpleItem>
            </GroupItem>
            {scheduleType === 'Monthly' && (
              <GroupItem>
                <SimpleItem dataField="daysOfMonth" editorType="dxTextBox">
                  <Label text={translate!('Days of month', transFormName)} />
                  <RequiredRule message={translate!('Days of month is required', transFormName)} />
                </SimpleItem>
              </GroupItem>
            )}
            <GroupItem>
              <SimpleItem dataField="periodToleranceMinus" editorType="dxNumberBox">
                <Label text={translate!('Tol. minus', transFormName)} />
              </SimpleItem>
            </GroupItem>
            <GroupItem>
              <SimpleItem dataField="periodTolerancePlus" editorType="dxNumberBox">
                <Label text={translate!('Tol. plus', transFormName)} />
              </SimpleItem>
            </GroupItem>
            <GroupItem
              visible={
                selectedUnit === BaseValueDataType.Int || selectedUnit === BaseValueDataType.Float
              }
            >
              <SimpleItem dataField="threshold" editorType="dxNumberBox">
                <Label text={translate!('Threshold', transFormName)} />
              </SimpleItem>
            </GroupItem>
            <GroupItem visible={selectedUnit === BaseValueDataType.Bool}>
              <SimpleItem
                dataField="threshold"
                editorType="dxSelectBox"
                editorOptions={{
                  items: [
                    { id: true, name: translate!('True', transFormName) },
                    { id: false, name: translate!('False', transFormName) },
                  ],
                  valueExpr: 'id',
                  displayExpr: 'name',
                }}
              >
                <Label text={translate!('Threshold', transFormName)} />
              </SimpleItem>
            </GroupItem>
            <GroupItem visible={selectedUnit === BaseValueDataType.Date}>
              <SimpleItem
                dataField="thresholdDate"
                editorType="dxDateBox"
                editorOptions={{
                  ...dateFieldOptionsWithSerialization,
                }}
              >
                <Label text={translate!('Threshold', transFormName)} />
                <CustomRule
                  validationCallback={validationNullableDateRule}
                  type={'custom'}
                  message={'Something bad with time'}
                  ignoreEmptyValue
                />
              </SimpleItem>
            </GroupItem>
            <GroupItem visible={selectedUnit === BaseValueDataType.DateTime}>
              <SimpleItem
                dataField="thresholdDateTime"
                editorType="dxDateBox"
                editorOptions={{
                  ...dateTimeFieldOptionsWithSerialization,
                }}
              >
                <CustomRule
                  validationCallback={validationNullableDateRule}
                  type={'custom'}
                  message={'Somethind bad with time'}
                  ignoreEmptyValue
                />
                <Label text={translate!('Threshold', transFormName)} />
              </SimpleItem>
            </GroupItem>
          </GroupItem>

          {scheduleType === 'Weekly' && (
            <GroupItem>
              <GroupItem>
                <hr />
              </GroupItem>
              <GroupItem colCount={3}>
                <SimpleItem dataField="monday" editorType="dxCheckBox">
                  <Label text={translate!('Monday', transFormName)} />
                </SimpleItem>
                <SimpleItem dataField="tuesday" editorType="dxCheckBox">
                  <Label text={translate!('Tuesday', transFormName)} />
                </SimpleItem>
                <SimpleItem dataField="wednesday" editorType="dxCheckBox">
                  <Label text={translate!('Wednesday', transFormName)} />
                </SimpleItem>
                <SimpleItem dataField="thursday" editorType="dxCheckBox">
                  <Label text={translate!('Thursday', transFormName)} />
                </SimpleItem>
                <SimpleItem dataField="friday" editorType="dxCheckBox">
                  <Label text={translate!('Friday', transFormName)} />
                </SimpleItem>
                <SimpleItem dataField="saturday" editorType="dxCheckBox">
                  <Label text={translate!('Saturday', transFormName)} />
                </SimpleItem>
                <SimpleItem dataField="sunday" editorType="dxCheckBox">
                  <Label text={translate!('Sunday', transFormName)} />
                </SimpleItem>
              </GroupItem>
              <GroupItem>
                <hr />
              </GroupItem>
            </GroupItem>
          )}

          <GroupItem>
            <SimpleItem dataField="visible" editorType="dxCheckBox">
              <Label text={translate!('Visible', transFormName)} />
            </SimpleItem>
          </GroupItem>
          <GroupItem colCount={4}>
            <GroupItem colSpan={2}></GroupItem>
            <ButtonItem>
              <ButtonOptions
                disabled={loading}
                type={'success'}
                text={translate!('Save', '')}
                useSubmitBehavior
              />
            </ButtonItem>
            <ButtonItem>
              <ButtonOptions type={'normal'} text={translate!('Close', '')} onClick={onClose} />
            </ButtonItem>
          </GroupItem>
        </Form>
      </form>
    </Popup>
  );
}
